/**
 * Filter Persistence Helper
 *
 * Manages persistence of filter state (search, chips, sort, pagination) to localStorage.
 * Follows the same pattern as layoutPersistence.js.
 *
 * Precedence: URL params > localStorage > defaults
 */

import {
  END_DATE,
  FILTER_PARAM,
  FILTER_STATE_VERSION,
  GALLERY_FILTER_PREFIX,
  getGalleryFilterKey,
  LOCAL_STORAGE_KEYS,
  PAGE,
  SEARCH_PARAM,
  SORT,
  START_DATE,
  VIEW_CONTEXT,
} from 'src/constants';
import { formatDateFilterValue, isDateField } from 'src/helpers/dateFields';
import type { SortData } from 'src/types/store.types';

/**
 * Date range for filter state
 */
export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

/**
 * Filter value structure for persistence
 */
export interface FilterValue {
  category: string;
  choice: string;
  value: string;
}

/**
 * Filter chip structure for viewState
 */
export interface FilterChip {
  category: string;
  choice: string;
  chip: string;
}

/**
 * Complete filter state structure for persistence
 */
export interface FilterState {
  searchTerm: string;
  filterValues: FilterValue[];
  sort: string | null;
  scope: string;
  page: number;
  dateRange: DateRange | null;
  version: number;
  savedAt: number | null;
}

/**
 * Partial filter state from URL parsing
 */
export interface UrlFilterState {
  searchTerm?: string;
  filterValues?: FilterValue[];
  sort?: string;
  scope?: string;
  page?: number;
  dateRange?: DateRange;
}

/**
 * View context scope type
 */
type ViewContextScope = typeof VIEW_CONTEXT.LIBRARY | typeof VIEW_CONTEXT.GALLERY;

/**
 * Default filter state shape
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  searchTerm: '',
  filterValues: [],
  sort: null,
  scope: 'all',
  page: 1,
  dateRange: null,
  version: FILTER_STATE_VERSION,
  savedAt: null,
};

/**
 * Get the localStorage key for a given scope
 *
 * @param scope - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery scope)
 * @returns localStorage key
 */
const getStorageKey = (scope: ViewContextScope, galleryId: string | null = null): string => {
  if (scope === VIEW_CONTEXT.GALLERY && galleryId) {
    return getGalleryFilterKey(galleryId);
  }
  return LOCAL_STORAGE_KEYS.LIBRARY_FILTERS;
};

/**
 * Validate filter state structure
 *
 * @param state - Filter state to validate
 * @returns Whether the state is valid
 */
export const isValidFilterState = (state: unknown): state is FilterState => {
  if (!state || typeof state !== 'object') return false;

  const s = state as Record<string, unknown>;

  // Check required fields exist with correct types
  if (typeof s.searchTerm !== 'string') return false;
  if (!Array.isArray(s.filterValues)) return false;
  if (typeof s.page !== 'number' || s.page < 1) return false;

  // Validate filter values structure
  for (const filter of s.filterValues as unknown[]) {
    if (!filter || typeof filter !== 'object') return false;
    const f = filter as Record<string, unknown>;
    if (typeof f.category !== 'string') return false;
    if (typeof f.choice !== 'string') return false;
    if (typeof f.value !== 'string') return false;
  }

  return true;
};

/**
 * Persist filter state to localStorage
 *
 * @param scope - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery scope)
 * @param filterState - Filter state to persist
 */
export const persistFilters = (
  scope: ViewContextScope,
  galleryId: string | null,
  filterState: Partial<FilterState>,
): void => {
  const stateToSave: FilterState = {
    ...DEFAULT_FILTER_STATE,
    ...filterState,
    version: FILTER_STATE_VERSION,
    savedAt: Date.now(),
  };

  try {
    const key = getStorageKey(scope, galleryId);
    window.localStorage.setItem(key, JSON.stringify(stateToSave));
  } catch (e) {
    // localStorage not available (private mode or blocked); ignore persistence
    console.warn('Filter persistence failed:', e);
  }
};

/**
 * Get raw persisted filter state from localStorage (no validation or side effects)
 *
 * @param scope - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery scope)
 * @returns Persisted filter state or null if not found/parse error
 */
export const getPersistedFilters = (scope: ViewContextScope, galleryId: string | null = null): FilterState | null => {
  try {
    const key = getStorageKey(scope, galleryId);
    const stored = window.localStorage.getItem(key);

    if (!stored) return null;

    return JSON.parse(stored) as FilterState;
  } catch {
    // localStorage not available or JSON parse error
    return null;
  }
};

/**
 * Get validated persisted filter state, clearing invalid state if found
 * Use this when you want to fetch and validate in one step with cleanup
 *
 * @param scope - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery scope)
 * @returns Valid persisted filter state or null
 */
export const getValidatedPersistedFilters = (
  scope: ViewContextScope,
  galleryId: string | null = null,
): FilterState | null => {
  const persisted = getPersistedFilters(scope, galleryId);

  if (!persisted) return null;

  if (!isValidFilterState(persisted)) {
    // Invalid state - clear it
    clearFilters(scope, galleryId);
    return null;
  }

  return persisted;
};

/**
 * Check if URL has any filter parameters
 *
 * @param urlSearchParams - URL search params
 * @returns Whether URL has filter params
 */
export const hasUrlFilters = (urlSearchParams: URLSearchParams): boolean => {
  const filterParams = [SEARCH_PARAM, FILTER_PARAM, SORT, PAGE, START_DATE, END_DATE, 'scope'];

  for (const param of filterParams) {
    if (urlSearchParams.has(param) && urlSearchParams.get(param)) {
      return true;
    }
  }
  return false;
};

/**
 * Parse URL search params to filter state
 *
 * @param urlSearchParams - URL search params
 * @returns Partial filter state from URL
 */
export const urlParamsToFilters = (urlSearchParams: URLSearchParams): UrlFilterState => {
  const filters: UrlFilterState = {};

  // Search term
  const term = urlSearchParams.get(SEARCH_PARAM);
  if (term) {
    filters.searchTerm = term;
  }

  // Filter values - multiple values
  const filterParams = urlSearchParams.getAll(FILTER_PARAM);
  if (filterParams.length > 0) {
    filters.filterValues = filterParams.map((value) => {
      // values are stored as "category:choice" format
      // Use indexOf to handle values that contain colons (e.g., "time:12:30:00")
      const colonIndex = value.indexOf(':');
      if (colonIndex === -1) {
        // Malformed filter value, use entire value as category
        return { category: value, choice: '', value };
      }
      const category = value.substring(0, colonIndex);
      const choice = value.substring(colonIndex + 1);
      return { category, choice, value };
    });
  }

  // Sort
  const sort = urlSearchParams.get(SORT);
  if (sort) {
    filters.sort = sort;
  }

  // Page
  const page = urlSearchParams.get(PAGE);
  if (page) {
    const pageNum = parseInt(page, 10);
    if (!Number.isNaN(pageNum) && pageNum >= 1) {
      filters.page = pageNum;
    }
  }

  // Scope
  const scope = urlSearchParams.get('scope');
  if (scope) {
    filters.scope = scope;
  }

  // Date range
  const startDate = urlSearchParams.get(START_DATE);
  const endDate = urlSearchParams.get(END_DATE);
  if (startDate || endDate) {
    filters.dateRange = { startDate, endDate };
  }

  return filters;
};

/**
 * Convert filter state to URL params
 *
 * @param filterState - Filter state
 * @returns URL search params
 */
export const filtersToUrlParams = (filterState: Partial<FilterState>): URLSearchParams => {
  const params = new URLSearchParams();

  if (filterState.searchTerm) {
    params.set(SEARCH_PARAM, filterState.searchTerm);
  }

  if (filterState.filterValues?.length && filterState.filterValues.length > 0) {
    filterState.filterValues.forEach((filter) => {
      params.append(FILTER_PARAM, filter.value);
    });
  }

  if (filterState.sort) {
    params.set(SORT, filterState.sort);
  }

  if (filterState.page && filterState.page > 1) {
    params.set(PAGE, String(filterState.page));
  }

  if (filterState.scope && filterState.scope !== 'all') {
    params.set('scope', filterState.scope);
  }

  if (filterState.dateRange) {
    if (filterState.dateRange.startDate) {
      params.set(START_DATE, filterState.dateRange.startDate);
    }
    if (filterState.dateRange.endDate) {
      params.set(END_DATE, filterState.dateRange.endDate);
    }
  }

  return params;
};

/**
 * Merge persisted state with URL state (URL wins on conflicts)
 *
 * @param persisted - Persisted filter state
 * @param urlFilters - Filters parsed from URL
 * @returns Merged filter state
 */
export const mergeFilterState = (persisted: FilterState | null, urlFilters: UrlFilterState): FilterState => {
  // Start with defaults
  const merged: FilterState = { ...DEFAULT_FILTER_STATE };

  // Apply persisted state
  if (persisted) {
    if (persisted.searchTerm) merged.searchTerm = persisted.searchTerm;
    if (persisted.filterValues?.length) merged.filterValues = persisted.filterValues;
    if (persisted.sort) merged.sort = persisted.sort;
    if (persisted.scope) merged.scope = persisted.scope;
    if (persisted.page) merged.page = persisted.page;
    if (persisted.dateRange) merged.dateRange = persisted.dateRange;
  }

  // URL params override persisted (URL always wins)
  if (urlFilters.searchTerm !== undefined) merged.searchTerm = urlFilters.searchTerm;
  if (urlFilters.filterValues !== undefined) merged.filterValues = urlFilters.filterValues;
  if (urlFilters.sort !== undefined) merged.sort = urlFilters.sort;
  if (urlFilters.scope !== undefined) merged.scope = urlFilters.scope;
  if (urlFilters.page !== undefined) merged.page = urlFilters.page;
  if (urlFilters.dateRange !== undefined) merged.dateRange = urlFilters.dateRange;

  return merged;
};

/**
 * Clear filters for a specific scope
 *
 * @param scope - VIEW_CONTEXT.LIBRARY or VIEW_CONTEXT.GALLERY
 * @param galleryId - Gallery ID (required for gallery scope)
 */
export const clearFilters = (scope: ViewContextScope, galleryId: string | null = null): void => {
  try {
    const key = getStorageKey(scope, galleryId);
    window.localStorage.removeItem(key);
  } catch {
    // localStorage not available; ignore
  }
};

/**
 * Clear ALL persisted filter keys (library + all galleries)
 * Used for full reset (e.g., Akomi logo click)
 */
export const clearAllPersistedFilters = (): void => {
  try {
    // Clear library filters
    window.localStorage.removeItem(LOCAL_STORAGE_KEYS.LIBRARY_FILTERS);

    // Clear all gallery filter keys (pattern: akomi.gallery.{id}.filters)
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(GALLERY_FILTER_PREFIX) && key.endsWith('.filters')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // localStorage not available; ignore
  }
};

/**
 * Field metadata for date display formatting in filter chips.
 */
export interface FieldDisplayMeta {
  fieldType?: string;
  dateDisplayStyle?: string;
}

/** Find an existing `..` range chip for a category in the filter chips array. */
export const getExistingRangeForCategory = (
  filterChips: Array<{ category: string; choice: string }>,
  slug: string,
): { from: string; to: string } | null => {
  const rangeChip = filterChips.find((c) => c.category === slug && c.choice.includes('..'));
  if (!rangeChip) return null;
  const [from, to] = rangeChip.choice.split('..');
  return { from, to };
};

/**
 * Build a single FilterChip from a URL-format filter string (e.g., "genre:rock").
 * Uses an optional slug→label map to produce human-readable chip labels,
 * and an optional slug→fieldMeta map for date display style formatting.
 *
 * @param filterString - URL-format filter string ("category:choice")
 * @param slugToLabel - Optional map of field slugs to display labels
 * @param slugToFieldMeta - Optional map of field slugs to display metadata
 * @returns FilterChip with proper display label
 */
export const buildFilterChip = (
  filterString: string,
  slugToLabel?: Map<string, string>,
  slugToFieldMeta?: Map<string, FieldDisplayMeta>,
): FilterChip => {
  const colonIdx = filterString.indexOf(':');
  if (colonIdx === -1) {
    return { category: filterString, choice: '', chip: filterString };
  }
  const categorySlug = filterString.substring(0, colonIdx);
  const choiceValue = filterString.substring(colonIdx + 1);
  const categoryLabel = slugToLabel?.get(categorySlug) || categorySlug;
  const meta = slugToFieldMeta?.get(categorySlug);
  if (isDateField(meta?.fieldType) && choiceValue.includes('..')) {
    const [from, to] = choiceValue.split('..');
    const fromDisplay = formatDateFilterValue(from, meta?.fieldType, meta?.dateDisplayStyle);
    const toDisplay = formatDateFilterValue(to, meta?.fieldType, meta?.dateDisplayStyle);
    return {
      category: categorySlug,
      choice: choiceValue,
      chip: `${categoryLabel}: ${fromDisplay} \u2013 ${toDisplay}`,
    };
  }
  const displayValue = formatDateFilterValue(choiceValue, meta?.fieldType, meta?.dateDisplayStyle);
  return {
    category: categorySlug,
    choice: choiceValue,
    chip: `${categoryLabel}: ${displayValue}`,
  };
};

/**
 * Convert filterValues (persistence format) to filterChips (viewState format)
 *
 * @param filterValues - Filter values from persistence
 * @param slugToLabel - Optional map of field slugs to display labels
 * @param slugToFieldMeta - Optional map of field slugs to display metadata
 * @returns Filter chips for viewState
 */
export const filterValuesToChips = (
  filterValues: FilterValue[] | undefined,
  slugToLabel?: Map<string, string>,
  slugToFieldMeta?: Map<string, FieldDisplayMeta>,
): FilterChip[] => {
  if (!filterValues || !Array.isArray(filterValues)) return [];
  return filterValues.map((filter) => buildFilterChip(filter.value, slugToLabel, slugToFieldMeta));
};

/**
 * View state interface for filter state building
 */
export interface ViewStateForFilters {
  searchTerm?: string;
  filterChips?: FilterChip[];
  sortData?: SortData;
  pagination?: { page?: number };
  ISOStartDate?: string;
  ISOEndDate?: string;
}

/**
 * Build filter state from current viewState
 * Maps viewState.filterChips to persistence format filterValues
 *
 * @param viewState - Current view state
 * @returns Filter state for persistence
 */
export const buildFilterStateFromViewState = (viewState: ViewStateForFilters): FilterState => {
  // Map filterChips from viewState to filterValues for persistence
  const filterValues = (viewState.filterChips || []).map((chip) => ({
    category: chip.category,
    choice: chip.choice,
    // Construct URL format same as projectToUrl: `${c.category}:${c.choice}`
    // NOTE: chip.chip is the DISPLAY format (e.g., "Producer: Annalee Jones"), not URL format
    value: `${chip.category}:${chip.choice}`,
  }));

  return {
    searchTerm: viewState.searchTerm || '',
    filterValues,
    sort: viewState.sortData?.sortBy?.value ? `${viewState.sortData.sortBy.value}:${viewState.sortData.orderBy}` : null,
    scope: 'all',
    page: viewState.pagination?.page || 1,
    dateRange:
      viewState.ISOStartDate || viewState.ISOEndDate
        ? { startDate: viewState.ISOStartDate || null, endDate: viewState.ISOEndDate || null }
        : null,
    version: FILTER_STATE_VERSION,
    savedAt: Date.now(),
  };
};
